import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import {
  useGlobalizationContext,
  useTranslation,
  useGetUserPermissions,
  useGetUiAdminConfigWritePermissions
} from '@cs-cms/state-management';
import { ABOUT_PAGE_SETTINGS_KEY } from '@cs-cms/types';
import { CpsTabs } from '../common/CpsTabs';
import { RoutePath } from '../types/RoutePath';

interface SettingsNavLink {
  to: string;
  text: string;
}

export const SettingsNavigation: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: permissions } = useGetUserPermissions();
  const { data: writePermissions } = useGetUiAdminConfigWritePermissions([ABOUT_PAGE_SETTINGS_KEY]);

  const viewsText = translate('custom.settings.views.title');
  const helpCenterText = translate('custom.settings.helpCenter.title');
  const adminCenterText = translate('custom.settings.adminCenter.title');
  const itAdminCenterText = translate('custom.settings.itAdminCenter.title');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { csvExport, ...adminPermissions } = permissions || {};
  const hasAdminAccess = Object.values(adminPermissions).some(Boolean);

  const availableLinks: SettingsNavLink[] = [
    {
      to: `/${RoutePath.SETTINGS}/${RoutePath.SETTINGS_VIEWS}`,
      text: viewsText
    },
    {
      to: `/${RoutePath.SETTINGS}/${RoutePath.SETTINGS_HELP_CENTER}`,
      text: helpCenterText
    },
    ...(hasAdminAccess
      ? [
          {
            to: `/${RoutePath.SETTINGS}/${RoutePath.SETTINGS_ADMIN_CENTER}`,
            text: adminCenterText
          }
        ]
      : []),
    ...(permissions?.itAdminSection && writePermissions?.[ABOUT_PAGE_SETTINGS_KEY] === true
      ? [
          {
            to: `/${RoutePath.SETTINGS}/${RoutePath.SETTINGS_IT_ADMIN_CENTER}`,
            text: itAdminCenterText
          }
        ]
      : [])
  ];

  const selectedTabIndex = availableLinks.findIndex(link => link.to === pathname);
  const selected = selectedTabIndex !== -1 ? selectedTabIndex : 0;

  const handleSelect = (index: number): void => {
    const link = availableLinks[index];
    if (link) {
      navigate(link.to);
    }
  };

  return (
    <CpsTabs
      ariaLabel="Settings"
      selected={selected}
      onSelect={handleSelect}
      items={availableLinks.map(link => ({
        id: link.to,
        title: link.text
      }))}
    />
  );
};
